"use client";

import { isIdentityGraphClientEnabled } from "@repo/identity";
import { syncSiteCmpConsentToServer } from "@shared/lib/consent/consent-server-sync";
import {
	applyServerCmpConsentRecord,
	clearSiteCmpConsentClientState,
	readCurrentCmpConsentRecord,
	reconcileLocalCmpFromServer,
} from "@shared/lib/consent/consent-sync-bridge";
import { orpcClient } from "@shared/lib/orpc-client";
import { useEffect, useState } from "react";
import {
	hasAnalyticsCaptureConsent,
	isAnalyticsConsentUnknown,
	subscribeAnalyticsConsent,
} from "../lib/analytics-consent-gate";
import {
	readSessionAttributionFromLocation,
	readSqidHandoffFromUrl,
	readSqSidCookie,
	writeSqSidCookie,
} from "../lib/identity-cookie";
import { linkSqidFromEmailDeepLink } from "../lib/identity-resolution";
import { persistPosthogDistinctIdLink } from "../lib/persist-posthog-distinct-id-link";
import { ActiveAnalyticsProvider } from "../lib/resolve-provider";
import { setVisitorBootstrapSnapshot } from "../lib/visitor-bootstrap-cache";
import * as Posthog from "../provider/posthog";

function clientCmpAnalyticsGranted(): boolean {
	const record = readCurrentCmpConsentRecord();
	return record?.cats.analytics === true;
}

interface IdentityBootstrapProps {
	onCaptureReady: () => void;
	onCaptureSuspended: () => void;
}

/**
 * Runs the identity graph + PostHog bootstrap when analytics consent is granted,
 * without requiring a full navigation: `ensureVisitor` (URL/cookie sqid merge),
 * cookie write, consent server sync, PostHog init + opt-in, deep-link alias, and
 * `onCaptureReady` so {@link AnalyticsRouteListener} can emit the first `$pageview`.
 * Consent choice events fire from {@link ConsentProvider} via the active provider.
 */
function useAnalyticsConsentForBootstrap(): {
	status: "unknown" | "decided";
	analyticsConsentGranted: boolean;
} {
	const [, tick] = useState(0);

	useEffect(() => subscribeAnalyticsConsent(() => tick((n) => n + 1)), []);

	return {
		status: isAnalyticsConsentUnknown() ? "unknown" : "decided",
		analyticsConsentGranted: hasAnalyticsCaptureConsent(),
	};
}

export function IdentityBootstrap({
	onCaptureReady,
	onCaptureSuspended,
}: IdentityBootstrapProps) {
	const { status, analyticsConsentGranted } =
		useAnalyticsConsentForBootstrap();

	useEffect(() => {
		if (status === "unknown") {
			return;
		}

		if (!analyticsConsentGranted) {
			if (ActiveAnalyticsProvider === Posthog) {
				Posthog.resetPosthogIdentityBootstrapState();
			}
			onCaptureSuspended();
			return;
		}

		let cancelled = false;

		async function bootstrapWithIdentityGraph() {
			const urlSqid = readSqidHandoffFromUrl();
			const cookieSqid = readSqSidCookie();
			const preferredSqid = urlSqid ?? cookieSqid ?? undefined;
			const attribution = readSessionAttributionFromLocation();

			const ensured = await orpcClient.identity.ensureVisitor({
				sqid: preferredSqid,
				...(urlSqid != null ? { sqidFromUrl: urlSqid } : {}),
				...(cookieSqid != null ? { sqidFromCookie: cookieSqid } : {}),
				attribution,
			});
			if (cancelled) {
				return;
			}

			if (ensured.cmpPolicy.isRetired) {
				clearSiteCmpConsentClientState();
				if (ActiveAnalyticsProvider === Posthog) {
					Posthog.resetPosthogIdentityBootstrapState();
				}
				onCaptureSuspended();
				return;
			}

			const reconciledRecord = reconcileLocalCmpFromServer({
				effectiveConsent: ensured.effectiveConsent,
				localRecord: readCurrentCmpConsentRecord(),
			});
			if (reconciledRecord != null) {
				applyServerCmpConsentRecord(reconciledRecord);
			}

			writeSqSidCookie(ensured.sqid);
			setVisitorBootstrapSnapshot({
				sqid: ensured.sqid,
				traitsForPosthog: ensured.traitsForPosthog,
			});

			await syncSiteCmpConsentToServer(ensured.sqid);

			if (cancelled) {
				return;
			}

			if (!clientCmpAnalyticsGranted()) {
				if (ActiveAnalyticsProvider === Posthog) {
					Posthog.resetPosthogIdentityBootstrapState();
				}
				onCaptureSuspended();
				return;
			}

			if (ActiveAnalyticsProvider === Posthog) {
				Posthog.setPosthogBootstrapDistinctId(ensured.sqid);
				Posthog.releasePosthogIdentityBootstrap();
				await Posthog.ensurePosthogClientForIdentityBootstrap();
			}

			const linkResult = await linkSqidFromEmailDeepLink({
				sqid: ensured.sqid,
				traits: ensured.traitsForPosthog,
				getCurrentDistinctId: async () => {
					if (ActiveAnalyticsProvider !== Posthog) {
						return ensured.sqid;
					}
					return Posthog.getDistinctId();
				},
			});

			if (ActiveAnalyticsProvider === Posthog) {
				Posthog.optInPosthogCapturingAfterIdentity();
			}

			await persistPosthogDistinctIdLink({
				sqid: ensured.sqid,
				getCurrentDistinctId: async () => {
					if (ActiveAnalyticsProvider !== Posthog) {
						return ensured.sqid;
					}
					return Posthog.getDistinctId();
				},
				priorDistinctId: linkResult.priorDistinctId,
			}).catch(() => undefined);

			if (!cancelled) {
				onCaptureReady();
			}
		}

		async function bootstrapWithoutIdentityGraph() {
			if (ActiveAnalyticsProvider === Posthog) {
				Posthog.resetPosthogIdentityBootstrapState();
			}

			try {
				const urlSqid = readSqidHandoffFromUrl();
				const cookieSqid = readSqSidCookie();
				const attribution = readSessionAttributionFromLocation();
				const ensured = await orpcClient.identity.ensureVisitor({
					sqid: urlSqid ?? cookieSqid ?? undefined,
					...(urlSqid != null ? { sqidFromUrl: urlSqid } : {}),
					...(cookieSqid != null
						? { sqidFromCookie: cookieSqid }
						: {}),
					attribution,
				});
				if (!cancelled) {
					writeSqSidCookie(ensured.sqid);
				}
			} catch {
				// Non-blocking when the identity graph client is disabled.
			}

			if (!cancelled) {
				onCaptureReady();
			}
		}

		if (!isIdentityGraphClientEnabled()) {
			void bootstrapWithoutIdentityGraph().catch(() => {
				if (!cancelled) {
					onCaptureSuspended();
				}
			});
			return () => {
				cancelled = true;
			};
		}

		void bootstrapWithIdentityGraph().catch(() => {
			if (!cancelled) {
				if (ActiveAnalyticsProvider === Posthog) {
					Posthog.resetPosthogIdentityBootstrapState();
				}
				onCaptureSuspended();
			}
		});

		return () => {
			cancelled = true;
		};
	}, [analyticsConsentGranted, onCaptureReady, onCaptureSuspended, status]);

	return null;
}
